import { useState, type FormEvent } from "react";
import { products } from "@/mocks/products";
import { site } from "@/config/site";
import OrderComplete from "./OrderComplete";

const SUBMIT_ADDR = "https://readdy.ai/api/form/dabtbu0ue8q5ojhuu3og";
const REGION_OPTIONS = ["通常地域（全国一律）", "北海道・沖縄・離島"];

const availableProducts = products.filter((p) => !("soldOut" in p && p.soldOut));

export default function OrderForm() {
  const [productId, setProductId] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [region, setRegion] = useState(REGION_OPTIONS[0]);
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [completedOrder, setCompletedOrder] = useState<{
    productLabel: string;
    quantity: number;
    productPrice: number;
    shipping: number;
    total: number;
    customerName: string;
  } | null>(null);

  const selectedProduct = availableProducts.find((p) => p.id === productId);
  const subtotal = selectedProduct ? selectedProduct.price * quantity : 0;
  const shipping = region === REGION_OPTIONS[1] ? 2500 : 2000;
  const total = subtotal + shipping;

  const productLabel = selectedProduct
    ? `${selectedProduct.name} ${selectedProduct.priceLabel}（${selectedProduct.weight}）`
    : "";

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    // ハニーポット（スパム対策）
    const honeypot = fd.get("website_alt");
    if (honeypot && String(honeypot).trim() !== "") {
      setStatus("success");
      form.reset();
      setProductId("");
      setQuantity(1);
      setRegion(REGION_OPTIONS[0]);
      return;
    }

    const price = selectedProduct?.price ?? 0;
    const productTotal = price * quantity;
    const shipFee = region === REGION_OPTIONS[1] ? 2500 : 2000;
    const grandTotal = productTotal + shipFee;

    fd.set("product", productLabel);
    fd.append("product_price", String(productTotal));
    fd.append("shipping", String(shipFee));
    fd.append("total", String(grandTotal));

    const body = new URLSearchParams();
    fd.forEach((value, key) => {
      if (key === "website_alt") return;
      body.append(key, String(value));
    });

    setStatus("submitting");
    setErrorMsg("");

    try {
      const res = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await res.text();
      let parsed: {
        code?: string;
        meta?: { message?: string; detail?: string };
        message?: string;
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const code = parsed?.code;
      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText;
      if (res.ok && code === "OK") {
        setStatus("success");
        setCompletedOrder({
          productLabel,
          quantity,
          productPrice: productTotal,
          shipping: shipFee,
          total: grandTotal,
          customerName: String(fd.get("name") || ""),
        });
      } else {
        setStatus("error");
        setErrorMsg(serverMsg || "送信に失敗しました。時間をおいて再度お試しください。");
      }
    } catch {
      setStatus("error");
      setErrorMsg("送信に失敗しました。時間をおいて再度お試しください。");
    }
  };

  const inputClass =
    "w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400";

  if (completedOrder) {
    return (
      <div className="max-w-3xl mx-auto">
        <OrderComplete
          productLabel={completedOrder.productLabel}
          quantity={completedOrder.quantity}
          productPrice={completedOrder.productPrice}
          shipping={completedOrder.shipping}
          total={completedOrder.total}
          customerName={completedOrder.customerName}
        />
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      data-readdy-form
      className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start"
    >
      {/* フォーム本体 */}
      <div className="lg:col-span-2 space-y-6">
        {/* ステップ1：商品選択 */}
        <section className="bg-background-100 rounded-lg border border-background-200 p-5 md:p-6">
          <h2 className="flex items-center gap-2 text-base font-heading font-bold text-foreground-950">
            <span className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-xs font-bold">
              1
            </span>
            商品選択
          </h2>
          <div className="mt-4">
            <label htmlFor="product" className="block text-sm font-bold text-foreground-700 mb-1.5">
              商品名
            </label>
            <select
              id="product"
              name="product"
              value={productId}
              onChange={(e) => setProductId(e.target.value)}
              required
              className={inputClass}
            >
              <option value="">商品をお選びください</option>
              {availableProducts.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} {p.priceLabel}（{p.weight}）
                </option>
              ))}
            </select>
          </div>
        </section>

        {/* ステップ2：数量 */}
        <section className="bg-background-100 rounded-lg border border-background-200 p-5 md:p-6">
          <h2 className="flex items-center gap-2 text-base font-heading font-bold text-foreground-950">
            <span className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-xs font-bold">
              2
            </span>
            数量選択
          </h2>
          <div className="mt-4">
            <label htmlFor="quantity" className="block text-sm font-bold text-foreground-700 mb-1.5">
              数量
            </label>
            <input
              id="quantity"
              name="quantity"
              type="number"
              min={1}
              value={quantity}
              onChange={(e) => setQuantity(Math.max(1, Number(e.target.value) || 1))}
              required
              className={`${inputClass} max-w-[160px]`}
            />
          </div>
        </section>

        {/* ステップ3：お客様情報 */}
        <section className="bg-background-100 rounded-lg border border-background-200 p-5 md:p-6">
          <h2 className="flex items-center gap-2 text-base font-heading font-bold text-foreground-950">
            <span className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-xs font-bold">
              3
            </span>
            お客様情報
          </h2>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="name" className="block text-sm font-bold text-foreground-700 mb-1.5">
                氏名 <span className="text-primary-600">*</span>
              </label>
              <input
                id="name"
                name="name"
                type="text"
                required
                placeholder="山田 花子"
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="phone" className="block text-sm font-bold text-foreground-700 mb-1.5">
                電話番号 <span className="text-primary-600">*</span>
              </label>
              <input
                id="phone"
                name="phone"
                type="tel"
                required
                placeholder="090-1234-5678"
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-bold text-foreground-700 mb-1.5">
                メールアドレス <span className="text-primary-600">*</span>
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                placeholder="example@mail.com"
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="postal" className="block text-sm font-bold text-foreground-700 mb-1.5">
                郵便番号 <span className="text-primary-600">*</span>
              </label>
              <input
                id="postal"
                name="postal"
                type="text"
                required
                placeholder="123-4567"
                className={inputClass}
              />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="address" className="block text-sm font-bold text-foreground-700 mb-1.5">
                住所 <span className="text-primary-600">*</span>
              </label>
              <input
                id="address"
                name="address"
                type="text"
                required
                placeholder="長崎県佐世保市〇〇町1-2-3"
                className={inputClass}
              />
            </div>
          </div>
        </section>

        {/* ステップ4：送料・お支払い */}
        <section className="bg-background-100 rounded-lg border border-background-200 p-5 md:p-6">
          <h2 className="flex items-center gap-2 text-base font-heading font-bold text-foreground-950">
            <span className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-xs font-bold">
              4
            </span>
            送料・お支払い方法
          </h2>
          <div className="mt-4">
            <label htmlFor="region" className="block text-sm font-bold text-foreground-700 mb-1.5">
              お届け先の地域
            </label>
            <select
              id="region"
              name="region"
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              className={inputClass}
            >
              {REGION_OPTIONS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
            <p className="mt-2 text-xs text-foreground-500">
              北海道・沖縄・離島への発送は、送料に＋500円かかります。
            </p>
          </div>
          <div className="mt-4">
            <label htmlFor="notes" className="block text-sm font-bold text-foreground-700 mb-1.5">
              備考欄
            </label>
            <textarea
              id="notes"
              name="notes"
              rows={4}
              maxLength={500}
              placeholder="ご要望などあればご記入ください（任意）"
              className={`${inputClass} resize-none`}
            />
          </div>
          <div className="mt-4 rounded-md bg-secondary-100 border border-secondary-200 p-4 text-sm text-foreground-700 space-y-2">
            <p className="font-bold">お支払い方法：銀行振込のみ</p>
            <p>ご注文確定後、下記口座へお振込みをお願いいたします。入金確認後、商品を発送いたします。</p>
            <div className="mt-2 bg-background-50 rounded border border-background-200 p-3 text-xs space-y-1">
              <p><span className="font-bold">銀行名：</span>{site.bankName}</p>
              <p><span className="font-bold">支店名：</span>{site.bankBranch}</p>
              <p><span className="font-bold">口座種別：</span>{site.bankAccountType}</p>
              <p><span className="font-bold">口座番号：</span>{site.bankAccountNumber}</p>
              <p><span className="font-bold">口座名義：</span>{site.bankAccountHolder}</p>
            </div>
          </div>
        </section>

        {/* ハニーポット */}
        <input
          type="text"
          name="website_alt"
          className="wpcf7-holder"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
        />

        {/* 送信ボタン */}
        <div>
          <button
            type="submit"
            disabled={status === "submitting"}
            className="inline-flex items-center justify-center gap-2 w-full bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-background-50 text-base font-bold px-6 py-4 rounded-md whitespace-nowrap transition-colors"
          >
            <i className="ri-shopping-bag-line"></i>
            {status === "submitting" ? "送信中..." : "注文を確定する"}
          </button>
        </div>

        {/* 送信結果 */}
        {status === "success" && (
          <div className="rounded-lg bg-accent-100 border border-accent-300 p-5 text-sm text-accent-900">
            <p className="font-bold flex items-center gap-2">
              <i className="ri-checkbox-circle-line"></i>
              ご注文を受け付けました。
            </p>
            <p className="mt-2">
              ご注文ありがとうございます。下記口座へお振込みをお願いいたします。入金確認後、商品を発送いたします。
            </p>
            <div className="mt-3 bg-background-50 rounded border border-background-200 p-3 text-xs space-y-1 text-foreground-700">
              <p><span className="font-bold">銀行名：</span>{site.bankName}</p>
              <p><span className="font-bold">支店名：</span>{site.bankBranch}</p>
              <p><span className="font-bold">口座種別：</span>{site.bankAccountType}</p>
              <p><span className="font-bold">口座番号：</span>{site.bankAccountNumber}</p>
              <p><span className="font-bold">口座名義：</span>{site.bankAccountHolder}</p>
            </div>
          </div>
        )}
        {status === "error" && (
          <div className="rounded-lg bg-primary-100 border border-primary-300 p-5 text-sm text-primary-900">
            <p className="font-bold flex items-center gap-2">
              <i className="ri-error-warning-line"></i>
              送信に失敗しました。
            </p>
            <p className="mt-2">{errorMsg}</p>
          </div>
        )}
      </div>

      {/* 注文内容サマリー */}
      <aside className="lg:col-span-1">
        <div className="lg:sticky lg:top-24 bg-background-100 rounded-lg border border-background-200 p-5 md:p-6">
          <h3 className="text-base font-heading font-bold text-foreground-950 mb-4 flex items-center gap-2">
            <i className="ri-receipt-line text-accent-700"></i>
            ご注文内容
          </h3>
          <dl className="space-y-3 text-sm">
            <div className="flex justify-between gap-3">
              <dt className="text-foreground-500 shrink-0">商品</dt>
              <dd className="text-foreground-900 text-right">{productLabel || "未選択"}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-foreground-500 shrink-0">数量</dt>
              <dd className="text-foreground-900">{quantity}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-foreground-500 shrink-0">商品価格</dt>
              <dd className="text-foreground-900">{subtotal.toLocaleString()}円</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-foreground-500 shrink-0">送料</dt>
              <dd className="text-foreground-900">{shipping.toLocaleString()}円</dd>
            </div>
            <div className="border-t border-background-200 pt-3 flex justify-between gap-3">
              <dt className="font-bold text-foreground-950">合計金額</dt>
              <dd className="font-heading font-bold text-xl text-primary-600">
                {total.toLocaleString()}円
              </dd>
            </div>
          </dl>
        </div>
      </aside>
    </form>
  );
}